// frontend/src/pages/admin/AdminCoinShopPage.tsx
// Backend /api/admin/content/coin-shop üzerinden service_role ile işlem yapar.
// Bu sayede RLS bypass edilir ve değişiklikler anında coin mağazasına yansır.

import { useEffect, useState } from 'react';
import { coinShopAdminService } from '../../services/admin.service';
import { supabase } from '../../lib/supabaseClient';

interface ShopItem {
  id: string;
  name: string;
  description: string;
  category: string;
  price_coins: number;
  image_emoji: string;
  image_url: string | null;
  is_active: boolean;
}

interface CoinShopOrder {
  id: string;
  purchased_at: string;
  shipping_name: string | null;
  shipping_address: string | null;
  shipping_phone: string | null;
  status: string;
  notes: string | null;
  student: { id: string; full_name: string } | null;
  item: { id: string; name: string; price_coins: number; category: string; image_emoji: string; image_url: string | null } | null;
}

const EMPTY = { name: '', description: '', category: 'avatar', price_coins: 100, image_emoji: '🎁', image_url: '' };
const CATS = ['avatar', 'theme', 'frame', 'pet', 'badge', 'physical'];
const CAT_TR: Record<string, string> = {
  avatar: 'Avatar',
  theme: 'Tema',
  frame: 'Çerçeve',
  pet: 'Evcil Hayvan',
  badge: 'Rozet',
  physical: 'Fiziksel Ürün',
};

const STATUS_TR: Record<string, string> = {
  pending: 'Hazırlanıyor',
  shipped: 'Kargolandı',
  delivered: 'Teslim Edildi',
};

export function AdminCoinShopPage() {
  const [adminTab, setAdminTab] = useState<'items' | 'orders'>('items');
  const [items, setItems] = useState<ShopItem[]>([]);
  const [orders, setOrders] = useState<CoinShopOrder[]>([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [editId, setEditId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Düzenlenen sipariş notu state'leri
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [tempNotes, setTempNotes] = useState('');

  async function reloadItems() {
    setIsLoading(true);
    try {
      setItems(await coinShopAdminService.getItems());
    } finally {
      setIsLoading(false);
    }
  }

  async function reloadOrders() {
    setIsLoadingOrders(true);
    try {
      setOrders(await coinShopAdminService.listOrders());
    } finally {
      setIsLoadingOrders(false);
    }
  }

  useEffect(() => {
    if (adminTab === 'items') {
      reloadItems();
    } else {
      reloadOrders();
    }
  }, [adminTab]);

  function openAdd() {
    setForm({ ...EMPTY });
    setEditId(null);
    setUploadProgress(0);
    setShowForm(true);
  }
  function openEdit(item: ShopItem) {
    setForm({
      name: item.name,
      description: item.description,
      category: item.category,
      price_coins: item.price_coins,
      image_emoji: item.image_emoji,
      image_url: item.image_url || '',
    });
    setEditId(item.id);
    setUploadProgress(0);
    setShowForm(true);
  }

  function flash(text: string, ok = true) {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  }

  async function handleUploadImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).slice(2, 9)}.${fileExt}`;
    const filePath = `${fileName}`;

    try {
      setUploadProgress(10);
      const { error: uploadError } = await supabase.storage
        .from('coin-shop-items')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      setUploadProgress(50);
      const { data: { publicUrl } } = supabase.storage
        .from('coin-shop-items')
        .getPublicUrl(filePath);

      setForm((f) => ({ ...f, image_url: publicUrl }));
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 1500);
    } catch (err) {
      alert('Resim yükleme başarısız: ' + (err instanceof Error ? err.message : 'Bilinmeyen Hata'));
      setUploadProgress(0);
    }
  }

  async function handleSave() {
    if (!form.name.trim()) {
      flash('İsim alanı zorunludur.', false);
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        image_url: form.image_url || null,
      };

      if (editId) {
        await coinShopAdminService.updateItem(editId, payload);
        flash('Ürün başarıyla güncellendi.');
      } else {
        await coinShopAdminService.addItem(payload);
        flash('Yeni ürün başarıyla eklendi.');
      }
      setShowForm(false);
      setEditId(null);
      reloadItems();
    } catch (err) {
      flash('İşlem başarısız: ' + (err instanceof Error ? err.message : 'Hata'), false);
    } finally {
      setIsSaving(false);
    }
  }

  async function toggleActive(item: ShopItem) {
    try {
      await coinShopAdminService.updateItem(item.id, { is_active: !item.is_active });
      flash(`"${item.name}" durumu güncellendi.`);
      reloadItems();
    } catch {
      flash('Güncelleme başarısız.', false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`"${name}" ürününü silmek istediğinize emin misiniz?`)) return;
    try {
      await coinShopAdminService.deleteItem(id);
      flash('Ürün silindi.');
      reloadItems();
    } catch {
      flash('Silme işlemi başarısız.', false);
    }
  }

  async function handleUpdateOrderStatus(orderId: string, status: string) {
    try {
      await coinShopAdminService.updateOrder(orderId, { status });
      flash('Sipariş durumu güncellendi.');
      reloadOrders();
    } catch (err) {
      flash('Sipariş güncellenemedi.', false);
    }
  }

  async function handleSaveNotes(orderId: string) {
    try {
      await coinShopAdminService.updateOrder(orderId, { notes: tempNotes });
      flash('Sipariş notu kaydedildi.');
      setEditingOrderId(null);
      reloadOrders();
    } catch (err) {
      flash('Not kaydedilemedi.', false);
    }
  }

  function startEditingNotes(order: CoinShopOrder) {
    setEditingOrderId(order.id);
    setTempNotes(order.notes || '');
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Coin Mağazası Yönetimi</h1>
        <p>Mağazadaki ürünleri düzenleyin ve fiziksel ürün siparişlerini takip edin.</p>
      </header>

      {msg && (
        <div
          className="quiz-error-banner"
          style={{
            background: msg.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            borderColor: msg.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)',
            color: msg.ok ? 'var(--secondary)' : 'var(--danger)',
            marginBottom: 16,
          }}
        >
          {msg.text}
        </div>
      )}

      {/* İki Katmanlı Sekmeler */}
      <div className="courses-tabs" style={{ marginBottom: 20 }}>
        <button className={`courses-tab ${adminTab === 'items' ? 'active' : ''}`} onClick={() => setAdminTab('items')}>
          Ürün Yönetimi
        </button>
        <button className={`courses-tab ${adminTab === 'orders' ? 'active' : ''}`} onClick={() => setAdminTab('orders')}>
          Sipariş Takibi ({orders.filter((o) => o.status !== 'delivered').length} Bekleyen)
        </button>
      </div>

      {/* ── ÜRÜN YÖNETİMİ SEKRESİ ── */}
      {adminTab === 'items' && (
        <>
          <button className="lesson-btn primary" style={{ marginBottom: 20 }} onClick={openAdd}>
            Yeni Ürün Ekle
          </button>

          {/* Form Modal */}
          {showForm && (
            <div className="admin-modal-backdrop" onClick={() => !isSaving && setShowForm(false)}>
              <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                <h3>{editId ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}</h3>

                <div className="form-group">
                  <label>Görsel Seçenekleri</label>
                  <div className="grid grid-2" style={{ gap: 20 }}>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--gray)' }}>Emoji Simgesi</label>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          className="input"
                          style={{ width: 80, fontSize: 18, textAlign: 'center' }}
                          value={form.image_emoji}
                          onChange={(e) => setForm((f) => ({ ...f, image_emoji: e.target.value }))}
                          maxLength={4}
                          placeholder="🎁"
                        />
                        <span style={{ fontSize: 11, color: 'var(--gray)' }}>Resim yüklenmediyse kullanılır.</span>
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--gray)' }}>Ürün Fotoğrafı Yükle</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadImage}
                        className="input"
                        style={{ border: 'none', background: 'transparent', padding: '4px 0', fontSize: 12 }}
                      />
                      {uploadProgress > 0 && <span style={{ fontSize: 11, color: 'var(--accent)' }}>Yükleniyor: %{uploadProgress}</span>}
                    </div>
                  </div>
                  {form.image_url && (
                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img src={form.image_url} alt="Ürün" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--dark-light)' }} />
                      <button
                        type="button"
                        className="lesson-btn secondary sm"
                        style={{ padding: '2px 6px', color: 'var(--danger)', fontSize: 11 }}
                        onClick={() => setForm((f) => ({ ...f, image_url: '' }))}
                      >
                        Görseli Kaldır
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>İsim</label>
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Ör: Termos Matara"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Açıklama</label>
                  <input
                    className="input"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Ürün açıklaması"
                  />
                </div>

                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Kategori</label>
                    <select className="input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                      {CATS.map((c) => (
                        <option key={c} value={c}>
                          {CAT_TR[c]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Coin Bedeli</label>
                    <input
                      className="input"
                      type="number"
                      min={10}
                      value={form.price_coins}
                      onChange={(e) => setForm((f) => ({ ...f, price_coins: Number(e.target.value) }))}
                    />
                  </div>
                </div>

                <div className="admin-modal-actions">
                  <button className="lesson-btn secondary" onClick={() => setShowForm(false)} disabled={isSaving}>
                    İptal
                  </button>
                  <button className="lesson-btn primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Kaydediliyor...' : editId ? 'Güncelle' : 'Ekle ve Yayınla'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="courses-loading">Ürünler yükleniyor...</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Görsel</th>
                    <th>İsim</th>
                    <th>Açıklama</th>
                    <th>Kategori</th>
                    <th>Coin</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--dark-light)' }} />
                        ) : (
                          <span style={{ fontSize: 28 }}>{item.image_emoji}</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 600 }}>{item.name}</td>
                      <td style={{ color: 'var(--gray)', fontSize: 13 }}>{item.description}</td>
                      <td>
                        <span
                          className="lesson-badge"
                          style={{
                            background: item.category === 'physical' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)',
                            color: item.category === 'physical' ? 'var(--amber)' : 'var(--primary-light)',
                          }}
                        >
                          {CAT_TR[item.category] ?? item.category}
                        </span>
                      </td>
                      <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{item.price_coins}</td>
                      <td>
                        <button
                          className={`lesson-btn sm ${item.is_active ? 'secondary' : 'primary'}`}
                          onClick={() => toggleActive(item)}
                        >
                          {item.is_active ? 'Aktif' : 'Pasif'}
                        </button>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="lesson-btn secondary sm" onClick={() => openEdit(item)}>
                            Düzenle
                          </button>
                          <button className="admin-remove-btn" onClick={() => handleDelete(item.id, item.name)}>
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray)', padding: 32 }}>
                        Henüz ürün yok.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── SİPARİŞ TAKİBİ SEKRESİ ── */}
      {adminTab === 'orders' && (
        <>
          {isLoadingOrders ? (
            <div className="courses-loading">Sipariş listesi yükleniyor...</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Öğrenci</th>
                    <th>Ürün</th>
                    <th>Bedel</th>
                    <th>Teslimat Bilgileri</th>
                    <th>Durum</th>
                    <th>Not</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} style={{ borderBottom: '1px solid var(--dark-light)' }}>
                      <td style={{ fontSize: 12, color: 'var(--gray)', whiteSpace: 'nowrap' }}>
                        {new Date(order.purchased_at).toLocaleDateString('tr-TR')}
                      </td>
                      <td style={{ fontWeight: 600 }}>{order.student?.full_name || 'Bilinmeyen Öğrenci'}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {order.item?.image_url ? (
                            <img src={order.item.image_url} alt={order.item.name} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />
                          ) : (
                            <span style={{ fontSize: 20 }}>{order.item?.image_emoji || '🎁'}</span>
                          )}
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{order.item?.name || 'Silinmiş Ürün'}</span>
                        </div>
                      </td>
                      <td style={{ color: 'var(--accent)', fontWeight: 700 }}>{order.item?.price_coins}</td>
                      <td style={{ fontSize: 12, maxWidth: 220, lineHeight: 1.4 }}>
                        {order.item?.category === 'physical' ? (
                          <div>
                            <strong>Alıcı:</strong> {order.shipping_name} <br />
                            <strong>Tel:</strong> {order.shipping_phone} <br />
                            <strong>Adres:</strong> {order.shipping_address}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--gray)' }}>Dijital Ürün</span>
                        )}
                      </td>
                      <td>
                        <span
                          className="lesson-badge"
                          style={{
                            background:
                              order.status === 'delivered'
                                ? 'rgba(16,185,129,0.15)'
                                : order.status === 'shipped'
                                ? 'rgba(59,130,246,0.15)'
                                : 'rgba(245,158,11,0.15)',
                            color:
                              order.status === 'delivered'
                                ? 'var(--secondary)'
                                : order.status === 'shipped'
                                ? 'var(--primary-light)'
                                : 'var(--amber)',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {STATUS_TR[order.status] ?? order.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, maxWidth: 200 }}>
                        {editingOrderId === order.id ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input
                              className="input sm"
                              value={tempNotes}
                              onChange={(e) => setTempNotes(e.target.value)}
                              style={{ padding: '2px 6px', fontSize: 11 }}
                            />
                            <button className="lesson-btn primary sm" onClick={() => handleSaveNotes(order.id)} style={{ padding: '2px 6px' }}>
                              Kaydet
                            </button>
                            <button className="lesson-btn secondary sm" onClick={() => setEditingOrderId(null)} style={{ padding: '2px 6px' }}>
                              İptal
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontStyle: order.notes ? 'normal' : 'italic', color: order.notes ? 'white' : 'var(--gray)' }}>
                              {order.notes || 'Not yok.'}
                            </span>
                            <button
                              className="lesson-btn secondary sm"
                              onClick={() => startEditingNotes(order)}
                              style={{ padding: '2px 4px', fontSize: 10, marginLeft: 6 }}
                              title="Notu Düzenle"
                            >
                              Düzenle
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
                          {order.item?.category === 'physical' && order.status === 'pending' && (
                            <button
                              className="lesson-btn primary sm"
                              style={{ fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }}
                              onClick={() => handleUpdateOrderStatus(order.id, 'shipped')}
                            >
                              Kargola
                            </button>
                          )}
                          {order.item?.category === 'physical' && order.status !== 'delivered' && (
                            <button
                              className="lesson-btn secondary sm"
                              style={{ fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }}
                              onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
                            >
                              Teslim Et
                            </button>
                          )}
                          {order.item?.category !== 'physical' && order.status !== 'delivered' && (
                            <button
                              className="lesson-btn secondary sm"
                              style={{ fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }}
                              onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
                            >
                              Teslim Edildi Yap
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray)', padding: 32 }}>
                        Henüz sipariş kaydı bulunmuyor.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Küçük toggle butonu bileşeni
function ToggleBtn({ active, onToggle, labels, colors }: { active: boolean; onToggle: () => void; labels: [string, string]; colors: [string, string] }) {
  return (
    <button
      onClick={onToggle}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 13,
        color: active ? colors[0] : colors[1],
        padding: '4px 8px',
      }}
    >
      {active ? labels[0] : labels[1]}
    </button>
  );
}
